import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  GetWorkSpaceSlotsDocument,
  SpaceControlBookingPoliciesDocument,
  WorkSpaceSlotFragment,
  WorkSpaceUpdatedSubscriptionDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, FormProvider } from 'modules/core/hooks/useForm'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useSubscription } from 'modules/core/hooks/useSubscription'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18nDateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import BookingSidepanel from 'modules/space_control/components/BookingsSidepanel/BookingSidepanel'
import { useDateBookingContext } from 'modules/space_control/components/dateBookingContext'
import { useBulkCreatelBooking } from 'modules/space_control/hooks/bookings/useBulkCreateBooking'
import { useWorkSpace } from 'modules/space_control/hooks/useWorkSpace'

interface bookingFormFields {
  accessId: string
  date: string
  slots: number[]
}

const AddBookingSidepanelPage = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { workSpaceId, date } = useParams()
  const context = useDateBookingContext()
  const contextDate = context?.date

  // Prefer URL param date, then context date, then today
  const bookingDate =
    date ||
    (contextDate ? i18nDateToString(contextDate, 'backend') : '') ||
    i18nDateToString(moment(), 'backend')
  const [currentDate, setCurrentDate] = useState<string>(bookingDate)
  const { workSpace } = useWorkSpace(workSpaceId ?? '')
  const { access: currentSessionAccess } = useCurrentSession()
  const [workSpaceSlots, setWorkSpaceSlots] = useState<WorkSpaceSlotFragment[]>([])
  const slotSetup = workSpace?.slotSetup
  const [bookedSlots, setBookedSlots] = useState<WorkSpaceSlotFragment[]>([])

  // Sync with context date changes
  useEffect(() => {
    if (!contextDate) return
    const newDate = i18nDateToString(contextDate, 'backend')
    if (newDate && newDate !== currentDate) {
      setCurrentDate(newDate)
    }
  }, [contextDate, currentDate])

  const { data: policiesData } = useQuery(SpaceControlBookingPoliciesDocument)
  const canBookSelf = policiesData?.spaceControl?._policies?.bookingEditSelf?.can ?? false

  const { loading, refetch: slotsRefetch } = useQuery(GetWorkSpaceSlotsDocument, {
    variables: {
      workSpaceId: workSpaceId ?? '',
      date: currentDate,
    },
    skip: !workSpaceId,
    onCompleted: (data) => {
      const _workSpaceSlots = data.spaceControl.workSpaceSlotsConnection.nodes
      setWorkSpaceSlots(_workSpaceSlots || [])
    },
  })

  useSubscription(WorkSpaceUpdatedSubscriptionDocument, {
    variables: { id: workSpaceId },
    onData: (data) => {
      if (Object.keys(data.data).length < 1) return

      slotsRefetch()
    },
  })

  useEffect(() => {
    setBookedSlots(workSpaceSlots.filter((slot) => slot.currentCapacity <= 0))
  }, [workSpace?.allowMultipleBookings, currentDate, loading, workSpaceSlots])

  const onClose = () => {
    track(trackingEvents.spaceControl.bookings.cancelBooking)
    navigate(navigation.spaceControl.overview.fullPath(), { state: { shouldRefetch: false } })
  }

  const defaultValues = useMemo(
    () => ({
      accessId: canBookSelf ? currentSessionAccess.id : '-1',
      date: i18nDateToString(moment(bookingDate), 'backend'),
      slots: new Array<number>(),
    }),
    [canBookSelf, currentSessionAccess.id, bookingDate]
  )

  const { bulkCreateSpacecontrolBooking } = useBulkCreatelBooking()

  const setFlashMessage = (success: boolean) => {
    const type = success ? 'correct' : 'wrong'
    const message = success
      ? i18n.t('space_control.overview.booking_sidepanel.submit_booking.success')
      : i18n.t('space_control.overview.booking_sidepanel.submit_booking.error')

    app.setFlash({
      type: type,
      message: message,
      time: 2000,
    })
  }

  const workSpaceID = workSpace?.id
  if (workSpaceID)
    return (
      <FormProvider
        enableReinitialize
        defaultValues={defaultValues}
        validate={({ date: inputDate, accessId, slots }) => {
          const errors: FormErrors<bookingFormFields> = {}
          if (!inputDate) {
            errors.date = i18n.t('common.empty')
          }
          if (!accessId || accessId === '-1') {
            errors.accessId = i18n.t('common.empty')
          }
          if (slotSetup !== 'whole_day' && (!slots || slots.length === 0)) {
            errors.slots = i18n.t('common.empty')
          }

          return errors
        }}
        onSubmit={async (values, { setErrors }) => {
          const result = await bulkCreateSpacecontrolBooking({
            ...values,
            workSpaceId: workSpaceID,
            date: values.date,
            slots: values.slots.length === 0 ? [1] : values.slots,
          })
          const handled = handleResult(result, (errors) => {
            if (errors) {
              setErrors(errors)
            }
          })

          setFlashMessage(handled.success)

          if (handled.success) {
            track(trackingEvents.spaceControl.bookings.publishBooking, {
              accessId: values.accessId,
              created_date: moment().toString(),
              booking_date: values.date,
              time_slot: workSpace?.slotSetup,
              number_slot: values.slots?.length,
              type_space: workSpace?.workSpaceTypeValue,
            })

            navigate(navigation.spaceControl.overview.fullPath(), {
              state: { shouldRefetch: true },
            })
          }

          return handled
        }}
      >
        <BookingSidepanel
          onClose={onClose}
          workSpace={workSpace}
          slots={workSpaceSlots}
          isSlotsLoading={loading}
          bookedSlots={bookedSlots}
          selectedDate={currentDate}
        />
      </FormProvider>
    )
  return <></>
}

export default AddBookingSidepanelPage
